import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreatePolicyApplicationErrors,
  PoliciesQueryBuilder,
  Policy,
  PolicyCreatedEnvelope,
  PolicyDeletedEnvelope,
  PolicyQuery,
  PolicyUpdatedEnvelope,
  QueryPoliciesResponse,
  ReorderEventPoliciesApplicationErrors,
  ReorderEventPoliciesOptions,
  ReorderEventPoliciesResponse,
  UpdatePolicy,
  createPolicy as universalCreatePolicy,
  deletePolicy as universalDeletePolicy,
  getPolicy as universalGetPolicy,
  queryPolicies as universalQueryPolicies,
  reorderEventPolicies as universalReorderEventPolicies,
  typedQueryPolicies as universalTypedQueryPolicies,
  updatePolicy as universalUpdatePolicy,
} from './events-v2-policy-policies.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function createPolicy(httpClient: HttpClient): CreatePolicySignature {
  return (policy: NonNullablePaths<Policy, `body` | `eventId` | `name`, 2>) =>
    universalCreatePolicy(
      policy,
      // @ts-ignore
      { httpClient }
    );
}

interface CreatePolicySignature {
  /**
   * Creates a policy.
   *
   *
   * <!--
   * >  Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
   * -->
   *
   * The `createPolicy()` function returns a Promise that resolves to the newly-created policy.
   *
   * You can create up to 3 policies per event. If you try to create more than 3, you'll get the "Maximum number of policies for the event has been reached" error.
   * @param - Policy info.
   * @returns Created policy.
   */
  (policy: NonNullablePaths<Policy, `body` | `eventId` | `name`, 2>): Promise<
    NonNullablePaths<Policy, `name` | `body` | `eventId`, 2> & {
      __applicationErrorsType?: CreatePolicyApplicationErrors;
    }
  >;
}

export function updatePolicy(httpClient: HttpClient): UpdatePolicySignature {
  return (
    _id: string,
    policy: NonNullablePaths<
      UpdatePolicy,
      `body` | `eventId` | `name` | `revision`,
      2
    >
  ) =>
    universalUpdatePolicy(
      _id,
      policy,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdatePolicySignature {
  /**
   * Updates a policy.
   *
   * <!--
   * > Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
   * -->
   *
   * The `updatePolicy()` function returns a Promise that resolves to the newly-updated policy.
   *
   * Each time the policy is updated, `revision` increments by 1. The existing `revision` must be included when updating the policy. This ensures you're working with the latest policy and prevents unintended overwrites.
   * @param - Policy to update.
   * @param - Policy ID.
   * @returns The updated policy.
   */
  (
    _id: string,
    policy: NonNullablePaths<
      UpdatePolicy,
      `body` | `eventId` | `name` | `revision`,
      2
    >
  ): Promise<NonNullablePaths<Policy, `name` | `body` | `eventId`, 2>>;
}

export function deletePolicy(httpClient: HttpClient): DeletePolicySignature {
  return (policyId: string) =>
    universalDeletePolicy(
      policyId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeletePolicySignature {
  /**
   * Permanently deletes a policy.
   *
   *
   * <!--
   * > Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
   * -->
   *
   * The `deletePolicy()` function returns a Promise that resolves when the specified policy is deleted.
   *
   * Deleted policies are not returned by the `getPolicy()` or `queryPolicies()` functions.
   * @param - Options for Delete Policy function.
   * @param - ID of the policy to delete.
   */
  (policyId: string): Promise<void>;
}

export function queryPolicies(httpClient: HttpClient): QueryPoliciesSignature {
  return () =>
    universalQueryPolicies(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPoliciesSignature {
  /**
   * Creates a query to retrieve a list of policies, given the provided paging and filter.
   *
   *
   * The `queryPolicies()` function builds a query to retrieve a list of policies and returns a [PoliciesQueryBuilder](https://www.wix.com/velo/reference/wix-events-v2/policies/policiesquerybuilder) object.
   *
   * The returned object contains the query definition which is typically used to run the query using the [`find()`](https://www.wix.com/velo/reference/wix-events-v2/policies/policiesquerybuilder/find) function.
   *
   * You can refine the query by chaining `PoliciesQueryBuilder` functions onto the query. `PoliciesQueryBuilder` functions enable you to sort, filter and control the results that `PoliciesQueryBuilder.find()` returns.
   *
   * The query runs with the following `PoliciesQueryBuilder` defaults that you can override:
   *
   * [`limit`](https://www.wix.com/velo/reference/wix-events-v2/policies/policiesquerybuilder/limit): `50`
   * [`descending`](https://www.wix.com/velo/reference/wix-events-v2/policies/policiesquerybuilder/descending): `_createdDate`
   *
   * The functions that are chained to `queryPolicies()` are applied in the order they are called. For example, if you sort on the `_createdDate` property in ascending order and then on the id property in descending order, the results are sorted by the created date and then, if there are multiple results with the same date, the items are sorted by the id.
   *
   * The table below shows which `PoliciesQueryBuilder` functions are supported for `queryPoliciesGuests()`. You can only use one filter function for each property. If a property is used in more than one filter, only the first filter will work.
   */
  (): PoliciesQueryBuilder;
}

export function typedQueryPolicies(
  httpClient: HttpClient
): TypedQueryPoliciesSignature {
  return (query: PolicyQuery) =>
    universalTypedQueryPolicies(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryPoliciesSignature {
  /** */
  (query: PolicyQuery): Promise<
    NonNullablePaths<
      QueryPoliciesResponse,
      | `policies`
      | `policies.${number}.name`
      | `policies.${number}.body`
      | `policies.${number}.eventId`,
      4
    >
  >;
}

export function reorderEventPolicies(
  httpClient: HttpClient
): ReorderEventPoliciesSignature {
  return (
    policyId: string,
    eventId: string,
    options?: ReorderEventPoliciesOptions
  ) =>
    universalReorderEventPolicies(
      policyId,
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ReorderEventPoliciesSignature {
  /**
   * Changes policy order in an event dashboard and agreement checkbox on the checkout form.
   * For example, if we have 3 policies in the list, after using this function the 3rd policy will become the 1st, and other policies will move by 1 position. By default, the policies are arranged by the created date in descending order.
   *
   * >  **Note**: it is possible to use both `beforePolicyId` and `afterPolicyId` at the same time but only the last one defined will be executed.
   *
   * <!--
   * >  Note: This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
   * -->
   *
   * The `reorderEventPolicies()` function returns a Promise that resolves to the newly-reordered policy.
   * @param - Event policy ID.
   * @param - Event ID.
   * @param - Options for Reorder Event Policies function.
   */
  (
    policyId: string,
    eventId: string,
    options?: ReorderEventPoliciesOptions
  ): Promise<
    NonNullablePaths<
      ReorderEventPoliciesResponse,
      | `policies`
      | `policies.${number}.name`
      | `policies.${number}.body`
      | `policies.${number}.eventId`,
      4
    > & {
      __applicationErrorsType?: ReorderEventPoliciesApplicationErrors;
    }
  >;
}

export function getPolicy(httpClient: HttpClient): GetPolicySignature {
  return (policyId: string) =>
    universalGetPolicy(
      policyId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPolicySignature {
  /**
   * Retrieves a policy by ID.
   *
   *
   * The `getPolicy()` function returns a Promise that resolves to a policy whose ID matches the given ID.
   * @param - Policy ID.
   * @returns The requested policy.
   */
  (policyId: string): Promise<
    NonNullablePaths<Policy, `name` | `body` | `eventId`, 2>
  >;
}

export const onPolicyCreated = EventDefinition(
  'wix.events.v2.policy_created',
  true,
  (event: PolicyCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<PolicyCreatedEnvelope>();
export const onPolicyDeleted = EventDefinition(
  'wix.events.v2.policy_deleted',
  true,
  (event: PolicyDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<PolicyDeletedEnvelope>();
export const onPolicyUpdated = EventDefinition(
  'wix.events.v2.policy_updated',
  true,
  (event: PolicyUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<PolicyUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  BaseEventMetadata,
  CreatePolicyRequest,
  CreatePolicyResponse,
  CursorPaging,
  Cursors,
  DeletePolicyRequest,
  DeletePolicyResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventCopied,
  EventMetadata,
  EventStatus,
  GetPolicyRequest,
  GetPolicyResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Location,
  LocationType,
  MapCoordinates,
  MessageEnvelope,
  Occurrence,
  Paging,
  PagingMetadataV2,
  PoliciesQueryBuilder,
  PoliciesQueryResult,
  Policy,
  PolicyCreatedEnvelope,
  PolicyDeletedEnvelope,
  PolicyQuerySpec,
  PolicyUpdatedEnvelope,
  QueryPoliciesRequest,
  QueryPoliciesResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  Recurrences,
  ReorderEventPoliciesOptions,
  ReorderEventPoliciesOptionsReferencePolicyOneOf,
  ReorderEventPoliciesRequest,
  ReorderEventPoliciesRequestReferencePolicyOneOf,
  ReorderEventPoliciesResponse,
  RestoreInfo,
  ScheduleConfig,
  SortOrder,
  Sorting,
  Status,
  StreetAddress,
  Subdivision,
  SubdivisionType,
  UpdatePolicy,
  UpdatePolicyRequest,
  UpdatePolicyResponse,
  UpdatePolicySortIndexRequest,
  UpdatePolicySortIndexResponse,
  WebhookIdentityType,
  utils,
} from './events-v2-policy-policies.universal.js';
